import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

// Types
export interface Announcement {
  id: string;
  agencyId: string;
  title: string;
  body: string;
  priority: 'normal' | 'urgent';
  isBlocking: boolean;
  /** Show this to agents who join after it was posted. */
  notifyNewAgents?: boolean;
  /** Only these people see it; null = everyone in the agency tree. */
  recipientUserIds?: string[] | null;
  createdBy: string;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  creator?: { id: string; firstName: string; lastName: string };
  acknowledgments?: AnnouncementAcknowledgment[];
  acknowledgmentCount?: number;
}

export interface AnnouncementAcknowledgment {
  id: string;
  announcementId: string;
  userId: string;
  acknowledgedAt: string;
  user?: { id: string; firstName: string; lastName: string; email: string };
}

export interface CreateAnnouncementRequest {
  title: string;
  body: string;
  priority?: 'normal' | 'urgent';
  isBlocking?: boolean;
  notifyNewAgents?: boolean;
  expiresAt?: string;
  /** Only these people; omit, null or empty = everyone. */
  recipientUserIds?: string[] | null;
}

/** Someone an announcement can be addressed to: anyone in the agency or a sub-agency. */
export interface AnnouncementAudienceMember {
  userId: string;
  name: string;
  email: string;
  role: string;
  agencyName: string;
}

export interface AnnouncementListResponse {
  announcements: Announcement[];
  total: number;
  page: number;
  totalPages: number;
}

export interface AnnouncementDetailResponse {
  announcement: Announcement;
  acknowledged: { userId: string; name: string; agencyName?: string | null; acknowledgedAt: string }[];
  pending: { userId: string; name: string; agencyName?: string | null }[];
}

// API Functions
export const getAnnouncements = async (agencyId: string, page = 1, limit = 20) => {
  const url = getApiUrl(`agencies/${agencyId}/announcements?page=${page}&limit=${limit}`);
  return handleApiCall<AnnouncementListResponse>(async () => {
    return await api.get<AnnouncementListResponse>({ url, auth: true });
  });
};

export const searchAnnouncementAudience = async (agencyId: string, q: string, ids: string[] = [], opts: { role?: "owners" | "all"; limit?: number } = {}) => {
  const params = new URLSearchParams({ q });
  if (ids.length) params.set("ids", ids.join(","));
  if (opts.role) params.set("role", opts.role);
  if (opts.limit) params.set("limit", String(opts.limit));
  const url = getApiUrl(`agencies/${agencyId}/announcements/audience?${params.toString()}`);
  return handleApiCall<AnnouncementAudienceMember[]>(async () => {
    return await api.get<AnnouncementAudienceMember[]>({ url, auth: true });
  });
};

export const getAnnouncementDetail = async (agencyId: string, announcementId: string) => {
  const url = getApiUrl(`agencies/${agencyId}/announcements/${announcementId}`);
  return handleApiCall<AnnouncementDetailResponse>(async () => {
    return await api.get<AnnouncementDetailResponse>({ url, auth: true });
  });
};

export const createAnnouncement = async (agencyId: string, data: CreateAnnouncementRequest) => {
  const url = getApiUrl(`agencies/${agencyId}/announcements`);
  return handleApiCall<Announcement>(async () => {
    return await api.post<Announcement>({ url, body: data, auth: true });
  });
};

export const updateAnnouncement = async (agencyId: string, announcementId: string, data: Partial<CreateAnnouncementRequest>) => {
  const url = getApiUrl(`agencies/${agencyId}/announcements/${announcementId}`);
  return handleApiCall<Announcement>(async () => {
    return await api.put<Announcement>({ url, body: data, auth: true });
  });
};

export const deleteAnnouncement = async (agencyId: string, announcementId: string) => {
  const url = getApiUrl(`agencies/${agencyId}/announcements/${announcementId}`);
  return handleApiCall<void>(async () => {
    return await api.delete<void>({ url, auth: true });
  });
};

export const acknowledgeAnnouncement = async (agencyId: string, announcementId: string) => {
  const url = getApiUrl(`agencies/${agencyId}/announcements/${announcementId}/acknowledge`);
  return handleApiCall<any>(async () => {
    return await api.post<any>({ url, body: {}, auth: true });
  });
};

export const getPendingAnnouncements = async () => {
  const url = getApiUrl('agencies/announcements/pending');
  return handleApiCall<Announcement[]>(async () => {
    return await api.get<Announcement[]>({ url, auth: true });
  });
};
